import { forwardRef, useCallback, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import useField from 'hooks/useField';
import KizenTypography from 'app/kizentypo';
import FileLibraryModal from 'components/Inputs/FileInput/Modal';
import ReadonlyFileLibraryModal from './Modal/ReadonlyModal';
import useModal from 'components/Modals/useModal';
import IconAdornment from 'components/Inputs/Adornments/IconAdornment';
import { resetButtonCss } from 'app/spacing';
import { cleanFiles } from 'components/Inputs/helpers';
import { MaybeInputControl } from '../InputControl';
import { UnderlineInputLayout } from '../TextInput/Underline';
import { useKeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import Validation, { useValidation } from '../Validation';
import { applyRef } from '../props';

const StyledPlaceholder = styled.div`
  display: flex;
  align-items: center;
`;

const FileInputWrapper = styled.button`
  ${resetButtonCss}
  ${({ disabled }) =>
    !disabled
      ? css`
          cursor: pointer;
        `
      : css`
          pointer-events: none;
        `}

  display: flex;
  flex: 1;
  width: 100%;

  > span {
    height: 100%;
  }

  > span:first-of-type {
    display: flex;
    flex: 1;
    align-content: center;
  }

  ${({ showVisibleIcon }) =>
    showVisibleIcon &&
    css`
      > span:last-of-type {
        display: flex;
        flex: 0 0 20px;

        align-content: center;
        justify-content: flex-end;
      }
    `}
`;

const FilledPlaceholder = ({ quantity = 0 }) => {
  const { t } = useTranslation();
  return (
    <StyledPlaceholder>
      <KizenTypography as="span" color="var(--color-button-secondary-default)">
        {`${quantity} ${
          quantity !== 1 ? t('Files Selected') : t('File Selected')
        }`}
      </KizenTypography>
    </StyledPlaceholder>
  );
};

const UnderlineFileInput = forwardRef((props, ref) => {
  const {
    fieldId,
    onChange,
    showVisibleIcon,
    fileLibraryOpen,
    fieldType,
    field,
    onErrorShow,
    onErrorHide,
    ...inputControlProps
  } = props;
  const {
    disabled,
    readonly,
    files: filesProp,
    initialFiles,
    viewable,
    source,
    publicFile = false,
    inModal,
    error,
    errorPlacement,
  } = props;
  const { assignFieldHandle } = useKeyBoardContext();

  const validationRef = useRef();
  const mergeRef = useCallback(
    (el) => {
      applyRef(validationRef, el);
      applyRef(ref, el);
    },
    [ref]
  );

  const [files] = useField(() => {
    return cleanFiles(filesProp);
  }, [filesProp]);
  const [focused, setFocused] = useState(false);
  const [validation, validationProps] = useValidation(validationRef, props);
  const [modalProps, , modal] = useModal({ initialValue: fileLibraryOpen });
  const disabledProp = disabled && !viewable;
  const buttonRef = useRef(null);
  assignFieldHandle(fieldId, {
    customFocus: () => {
      buttonRef.current?.focus();
      return buttonRef.current;
    },
    disabled: disabledProp,
  });

  return (
    <>
      <MaybeInputControl
        forInput
        variant="underline"
        {...{
          ...inputControlProps,
          error: true,
          disabled: true,
          shrink: true,
        }}
      >
        <UnderlineInputLayout
          ref={mergeRef}
          disabled={disabledProp}
          viewable={viewable}
          focused={focused}
          error={error || validation.error}
        >
          <FileInputWrapper
            ref={buttonRef}
            data-qa-file-input="open-button"
            onClick={() => (!disabled || viewable) && modal.show()}
            disabled={disabledProp}
            showVisibleIcon={showVisibleIcon}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          >
            <span>
              <FilledPlaceholder quantity={files.length} />
            </span>
            {showVisibleIcon && (
              <span>
                {(!disabled || viewable) && <IconAdornment icon="visible" />}
              </span>
            )}
          </FileInputWrapper>
        </UnderlineInputLayout>
        <Validation
          inModal={inModal}
          errorPlacement={errorPlacement}
          onErrorShow={onErrorShow}
          onErrorHide={onErrorHide}
          {...validationProps}
        />
      </MaybeInputControl>
      {modalProps.show && !readonly && (
        <FileLibraryModal
          show
          files={files}
          initialFiles={initialFiles}
          source={source}
          onChange={onChange}
          modalProps={modalProps}
          modal={modal}
          disabled={disabled}
          viewable={viewable}
          publicFile={publicFile}
          field={field}
          hasUnsavedConfirmation
        />
      )}
      {modalProps.show && readonly && (
        <ReadonlyFileLibraryModal
          files={files}
          onHide={modal.hide}
          viewable={viewable}
          {...modalProps}
        />
      )}
    </>
  );
});

UnderlineFileInput.defaultProps = {
  label: null,
  id: '',
  className: '',
  error: false,
  disabled: false,
  shrink: null,
  viewable: true,
  readonly: false,
  showVisibleIcon: true,
};
export default UnderlineFileInput;
